import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Crown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/pricing";
import { apiMessage } from "@/lib/team";
import { StatusChip } from "@/components/admin/team/TeamTable";
import { allowedActions } from "@/components/admin/team/TeamRowActions";
import { TeamActionDialog } from "@/components/admin/team/TeamActionDialog";
import { MemberHistory, RecentActivity } from "@/components/admin/team/MemberHistory";
import { SuperAdminOnly, TwoFactorNotice } from "@/pages/admin/team/TeamList";

const LABELS = { role: "Change role", suspend: "Suspend", reactivate: "Reactivate", revoke: "Revoke access" };

function Fact({ label, value, testid }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-widest text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900 break-words" data-testid={testid}>{value || "—"}</dd>
    </div>
  );
}

export default function TeamMemberDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const allowed = user?.role === "super_admin";

  const load = useCallback(() => {
    setError("");
    api.get(`/admin/team/members/${id}`).then((r) => setData(r.data)).catch((e) => setError(apiMessage(e, "Couldn't load this team member.")));
    api.get("/auth/me").then((r) => setIsOwner(!!r.data.is_owner)).catch(() => setIsOwner(false));
  }, [id]);
  useEffect(() => { if (allowed) load(); }, [load, allowed]);

  if (!allowed) return <SuperAdminOnly />;
  const m = data?.member;
  const actions = m ? allowedActions(m, user, isOwner) : [];
  const lrc = data?.last_role_change;

  return (
    <div data-testid="team-member-detail">
      <Link to="/admin/team" className="text-sm text-cyan-700 hover:underline inline-flex items-center gap-1" data-testid="team-detail-back"><ArrowLeft className="w-4 h-4" /> Cyannect Team</Link>
      {error && <div className="mt-6 pf-card p-6 text-sm text-red-700" role="alert" data-testid="team-detail-error">{error}</div>}
      {!data && !error && <div className="mt-10 flex justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {m && (<>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900 break-words" data-testid="team-detail-name">{m.name || m.email}</h2>
            <div className="mt-2 flex gap-2 flex-wrap items-center">
              <StatusChip status={m.status} testid="team-detail-status" />
              <span className="pf-chip pf-chip-lav" data-testid="team-detail-role">{m.role_label}</span>
              {m.is_owner && <span className="pf-chip pf-chip-cyan"><Crown className="w-3 h-3" /> Main owner</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {actions.map((a) => (
              <button key={a} type="button" onClick={() => setAction({ kind: a, row: m })} data-testid={`team-detail-action-${a}`}
                className={`pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold ${["suspend", "revoke"].includes(a) ? "text-red-700" : ""}`}>{LABELS[a]}</button>
            ))}
          </div>
        </div>
        <dl className="pf-card p-6 mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Fact label="Email" value={m.email} testid="team-detail-email" />
          <Fact label="Invited by" value={m.invited_by === "system@cyannect" ? "Automatic (owner setup)" : m.invited_by} />
          <Fact label="Invitation date" value={m.invited_at && dateTime(m.invited_at)} />
          <Fact label="Last login" value={m.last_login_at ? dateTime(m.last_login_at) : "Never"} testid="team-detail-last-login" />
          <Fact label="Created" value={m.created_at && dateTime(m.created_at)} />
          <Fact label="Last role change" value={lrc ? `${dateTime(lrc.created_at)} by ${lrc.actor_email}` : "No changes yet"} testid="team-detail-last-role-change" />
          <Fact label="Two-step sign-in" value="Not enforced yet" testid="team-detail-2fa" />
          <div className="sm:col-span-2"><Fact label="Internal notes" value={m.notes} /></div>
        </dl>
        <TwoFactorNotice />
        <section className="pf-card p-6 mt-6" data-testid="team-detail-permissions">
          <h3 className="font-semibold text-slate-900">What this role can do</h3>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            {data.permissions.map((p) => <span key={p} className="pf-chip font-mono text-[11px]">{p}</span>)}
          </div>
        </section>
        <MemberHistory rows={data.history} />
        <RecentActivity rows={data.recent_activity} />
      </>)}
      <TeamActionDialog action={action} actorIsOwner={isOwner} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}
